import React, { FC, useEffect, useRef, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import IconArrowLeft from 'src/assets/images/icons/arrow-left.svg';
import IconSearch from 'src/assets/images/icons/search-settings.svg';
import { TreeItem as TreeItemType, TreeMenuProps } from './types';
import { TreeList } from './TreeList';
import { useTreeSelection } from './useTreeSelection';

export const TreeMenu: FC<TreeMenuProps> = ({
  items,
  config,
  isVisible = false,
  isLoading = false,
  selectedValues = [],
  onClose,
  onApply,
  onToggle,
  renderItem,
  isMultiSelect = true,
  toggleButtonRef,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Create refs for the menu element and toggle button
  const menuRef = useRef<HTMLDivElement>(null);
  // Use external toggleButtonRef if provided, otherwise create internal one
  const internalToggleButtonRef = useRef<HTMLButtonElement>(null);
  const activeToggleButtonRef = toggleButtonRef || internalToggleButtonRef;

  // Use tree selection hook
  const {
    items: treeItems,
    setItems,
    toggleMainItem,
    toggleSubItem,
    toggleSubSubItem,
    selectAll,
    clearAll,
    getSelectedValues,
    hasSelectedItems,
  } = useTreeSelection(items, !isMultiSelect);

  // Clear search term when menu closes
  useEffect(() => {
    if (!isVisible) {
      setSearchTerm('');
    }
  }, [isVisible]);

  // Click outside handler to close the menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const isToggleButton = activeToggleButtonRef.current?.contains(event.target as Node);

      if (!isToggleButton && menuRef.current && !menuRef.current.contains(event.target as Node) && isVisible) {
        event.preventDefault();
        onClose();
      }
    };

    if (isVisible) {
      // Add a small delay before enabling the click outside handler
      // This prevents the menu from closing immediately when the toggle button is clicked
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, onClose, activeToggleButtonRef]);

  // Update items when external items change
  useEffect(() => {
    if (items.length > 0) {
      setItems(items);
    }
  }, [items, setItems]);

  // Filter items based on search term
  const filterItemsRecursively = (items: TreeItemType[]): TreeItemType[] => {
    if (!searchTerm.trim()) return items;

    return items
      .map((item) => {
        // Check if this item matches the search term
        const matchesMain = item.label.toLowerCase().includes(searchTerm.toLowerCase());

        // Also check value if it's a string
        const matchesValue = typeof item.value === 'string' && item.value.toLowerCase().includes(searchTerm.toLowerCase());

        // Filter sub-items recursively
        let filteredSubItems: TreeItemType[] = [];
        if (item.subItems && item.subItems.length > 0) {
          filteredSubItems = filterItemsRecursively(item.subItems);
        }

        // If this item matches or has matching sub-items, include it
        if (matchesMain || matchesValue || filteredSubItems.length > 0) {
          return {
            ...item,
            subItems: filteredSubItems,
          };
        }

        return null;
      })
      .filter(Boolean) as TreeItemType[];
  };

  // Apply the recursive filtering while preserving selection state
  const filteredItems = filterItemsRecursively(treeItems);

  const handleApply = () => {
    const selectedValues = getSelectedValues();

    if (onApply) {
      onApply(selectedValues);
    }
    onClose();
  };

  // Helper to find an item in the full tree and determine its actual hierarchy
  const findItemInTree = (
    itemId: string | number,
    items: TreeItemType[],
    parentId?: string | number,
    grandParentId?: string | number
  ): { item: TreeItemType; level: 'main' | 'sub' | 'subSub'; parentId?: string | number; grandParentId?: string | number } | null => {
    for (const item of items) {
      // Check if this is the item we're looking for
      if (item.id === itemId) {
        if (grandParentId) {
          return { item, level: 'subSub', parentId, grandParentId };
        } else if (parentId) {
          return { item, level: 'sub', parentId };
        } else {
          return { item, level: 'main' };
        }
      }

      // Search in sub-items
      if (item.subItems && item.subItems.length > 0) {
        const found = findItemInTree(itemId, item.subItems, item.id, parentId);
        if (found) {
          return found;
        }
      }
    }

    return null;
  };

  // Helper to determine the correct toggle handler based on item hierarchy
  const handleItemToggle = (itemId: string | number) => {
    // Find the item in the full tree structure (not filtered)
    const result = findItemInTree(itemId, treeItems);

    if (!result) {
      return;
    }

    const { level, parentId, grandParentId } = result;

    // For single select mode, we need to call onApply after the selection is made
    // Since setState is async, we can't rely on return values
    if (!isMultiSelect) {
      // Perform the selection
      if (level === 'subSub' && grandParentId && parentId) {
        toggleSubSubItem(grandParentId, parentId, itemId);
      } else if (level === 'sub' && parentId) {
        toggleSubItem(parentId, itemId);
      } else {
        toggleMainItem(itemId);
      }

      // For single select, immediately call onApply with the selected item's value
      // We know exactly what was selected - the itemId
      const selectedItem = result.item;
      if (onApply) {
        onApply([selectedItem.value]);
      }
      onClose();
    } else {
      // For multi-select mode, just toggle without immediate action
      if (level === 'subSub' && grandParentId && parentId) {
        toggleSubSubItem(grandParentId, parentId, itemId);
      } else if (level === 'sub' && parentId) {
        toggleSubItem(parentId, itemId);
      } else {
        toggleMainItem(itemId);
      }
    }
  };

  // Helper function to render content based on loading state and data availability
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className='flex items-center justify-center h-full'>
          <CircularProgress size={32} />
        </div>
      );
    }

    if (filteredItems.length === 0) {
      return <div className='flex items-center justify-center h-full text-neutral-60'>No items found</div>;
    }

    return (
      <TreeList
        items={filteredItems}
        level='main'
        onToggleMain={(id) => {
          handleItemToggle(id);
        }}
        onToggleSub={(mainId, subId) => {
          handleItemToggle(subId);
        }}
        onToggleSubSub={(mainId, subId, subSubId) => {
          handleItemToggle(subSubId);
        }}
        renderItem={renderItem}
        isMultiSelect={isMultiSelect}
      />
    );
  };

  // Default config values
  const { title, searchPlaceholder = 'Search', width = '380px', maxHeight = '80vh', showSearch = true, showSelectAll = true, showClear = true } = config;

  return (
    <div
      ref={menuRef}
      className={`absolute right-0 top-[64px] z-[999990] bg-white text-blue_grey-90 border border-[#DFDFDF] rounded-8 shadow-lg transform transition-all duration-300 ease-in-out ${
        isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-20 pointer-events-none'
      }`}
      style={{ width, maxHeight }}
    >
      <form className='relative flex flex-col h-full'>
        <div className='flex flex-col'>
          <div className='flex items-center justify-between p-16'>
            <div className='flex items-center gap-8'>
              <button
                type='button'
                ref={!toggleButtonRef ? internalToggleButtonRef : undefined}
                onClick={() => {
                  if (onToggle) onToggle();
                  setTimeout(() => {
                    onClose();
                  }, 50);
                }}
                aria-label='back'
                className='p-4 hover:bg-neutral-10 rounded-full transition-colors duration-200'
              >
                <img src={IconArrowLeft} alt='back' />
              </button>
              <p className='text-16 font-medium'>{title}</p>
            </div>
            {isMultiSelect && (showSelectAll || showClear) && (
              <div className='flex items-center gap-16'>
                {showSelectAll && (
                  <button type='button' onClick={selectAll} className='text-12 xl:text-14 font-medium text-primary'>
                    Select All
                  </button>
                )}
                {showSelectAll && showClear && <div className='w-1 h-12 bg-neutral-20' />}
                {showClear && (
                  <button type='button' onClick={clearAll} className='text-12 xl:text-14 font-medium text-neutral-60'>
                    Clear
                  </button>
                )}
              </div>
            )}
          </div>
          {showSearch && (
            <div className='pt-4 pb-16 px-16 border-b border-neutral-20'>
              <div className='relative'>
                <img src={IconSearch} alt='search' className='absolute left-16 top-1/2 -translate-y-1/2' />
                <input
                  type='text'
                  placeholder={searchPlaceholder}
                  className='w-full pl-48 pr-16 py-12 bg-neutral-10 border border-neutral-20 rounded-8'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>
        <div
          className='overflow-y-auto px-16 py-12 flex-1'
          style={{
            maxHeight: showSearch ? 'calc(80vh - 220px)' : 'calc(80vh - 160px)',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {renderContent()}
        </div>
        {isMultiSelect && (
          <div className='p-16 rounded-b-8 bg-neutral-10 border-t border-neutral-20'>
            <Button
              variant='contained'
              fullWidth
              onClick={handleApply}
              disabled={filteredItems.length === 0 || !hasSelectedItems}
              sx={{
                '&.Mui-disabled': {
                  color: '#FFFFFF',
                  cursor: 'not-allowed',
                  opacity: 0.9,
                },
              }}
            >
              Apply
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};
